/**
 * The server list's LAN address control (cleanup wave G), as plain data: where a node's address comes from in words,
 * the choices it offers — auto, each address the node reported with the warning `lanIpProblem` gives it, another
 * address typed in — and whether a typed one may be sent (a private IPv4: the cloud publishes it under our zone, D16).
 * Pure: `components/admin/LanAddressControl.tsx` renders it.
 */
import { isPrivateIpv4, lanIpProblem, type LanIpProblem, type SiteNodeWire } from '@acp/api-contract/sites.js';

type LanFields = Pick<SiteNodeWire, 'lanIp' | 'lanIpSetting' | 'lanCandidates' | 'lanIpSource'>;

/** A few words per problem, for a candidate's line (the full advice is the node's warning under its name). */
export const LAN_PROBLEM_LABEL: Readonly<Record<LanIpProblem, string>> = {
  'docker-desktop': 'Docker Desktop’s VM — not reachable from the LAN',
  'docker-bridge': 'Docker’s bridge — not reachable from the LAN',
  'vm-nat': 'a VM’s NAT adapter — not reachable from the LAN',
  'link-local': 'link-local — not a LAN address',
  loopback: 'loopback — this machine only',
};

/** Where the address in effect comes from, in words (an open enum: a newer node's value reads as unknown). */
export function lanSourceText(node: LanFields): string {
  switch (node.lanIpSource) {
    case 'environment':
      return 'set on the server (ACP_LAN_IP)';
    case 'cloud':
      return 'chosen here';
    case 'detected':
      return 'auto';
    case undefined:
      return 'not reported';
    default:
      return 'unknown source (a newer release?)';
  }
}

export type LanOption = {
  /** The address, or null for auto. */
  readonly value: string | null;
  readonly label: string;
  /** The interface it is on (a candidate), or null. */
  readonly interface: string | null;
  /** Why it will not work for the pair (`lanIpProblem`), or null. */
  readonly warning: string | null;
  /** It is the address the node registers now. */
  readonly inUse: boolean;
  /** The cloud accepts it (a private IPv4: it is published in DNS under our name, D16); a public or link-local one is shown, not offered. */
  readonly choosable: boolean;
};

/** Auto first, then every address the node reported (in its order), each once. */
export function lanOptions(node: LanFields): LanOption[] {
  const seen = new Set<string>();
  const candidates = (node.lanCandidates ?? []).filter((c) => {
    if (seen.has(c.ip)) return false;
    seen.add(c.ip);
    return true;
  });
  return [
    { value: null, label: 'Auto — the server’s own pick', interface: null, warning: null, inUse: false, choosable: true },
    ...candidates.map((c) => {
      const problem = lanIpProblem(c.ip);
      const choosable = isChoosableLanIp(c.ip);
      const warning = problem !== null ? LAN_PROBLEM_LABEL[problem] : choosable ? null : 'not a private address — cannot be published';
      return { value: c.ip, label: c.ip, interface: c.interface, warning, inUse: c.ip === node.lanIp, choosable };
    }),
  ];
}

/** What the control starts on: the setting — a reported address, auto, or (a typed one) "other". */
export function initialChoice(node: LanFields): { readonly kind: 'option'; readonly value: string | null } | { readonly kind: 'other'; readonly text: string } {
  const setting = node.lanIpSetting ?? null;
  if (setting === null || (node.lanCandidates ?? []).some((c) => c.ip === setting)) return { kind: 'option', value: setting };
  return { kind: 'other', text: setting };
}

/** A typed address the cloud accepts: dotted IPv4, each part 0–255, in a private range (10/8, 172.16/12, 192.168/16, 100.64/10). */
export function isChoosableLanIp(text: string): boolean {
  const parts = text.trim().split('.');
  // No leading zeros (`192.168.01.5`): the contract's `z.ipv4()` refuses them.
  if (parts.length !== 4 || !parts.every((p) => /^(0|[1-9]\d{0,2})$/.test(p) && Number(p) <= 255)) return false;
  return isPrivateIpv4(text.trim());
}
